import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import { Button, Card, Chip, Flex, Icon, Text } from '@servicetitan/anvil2';
import IconArrowForwardIos from '@servicetitan/anvil2/assets/icons/material/round/arrow_forward_ios.svg';
import { provide, useDependencies } from '@servicetitan/react-ioc';
import { Fab, FabButton, FabIcon, ionIcons, Page, Spinner } from '@servicetitan/web-feature-ui';

import { Content } from '../../common/components/content';
import { ErrorState } from '../../common/components/error-state';
import { RequestState } from '../../common/utils/request-states';
import { ServiceEntriesDataStore } from '../stores/service-entries-data.store';
import { ServiceEntriesViewStore } from '../stores/service-entries-view.store';

export const ServiceEntriesPage = provide({
    singletons: [ServiceEntriesViewStore, ServiceEntriesDataStore],
})(
    observer(() => {
        const [{ fetchServiceEntries, cancelGetServiceEntries, fetchServiceEntriesRequestState }] =
            useDependencies(ServiceEntriesDataStore);
        const [{ serviceEntries }] = useDependencies(ServiceEntriesViewStore);
        const history = useHistory();

        const handleTryAgain = () => {
            fetchServiceEntries();
        };

        const handleGoBack = () => {
            history.goBack();
        };

        useEffect(() => {
            fetchServiceEntries();

            return cancelGetServiceEntries;
        }, [fetchServiceEntries, cancelGetServiceEntries]);

        if (fetchServiceEntriesRequestState === RequestState.Loading) {
            return (
                <Content justifyContent="center" alignItems="center">
                    <Flex direction="column" gap="5" alignItems="center">
                        <Spinner size="3.75rem" />
                        <Flex direction="column" gap="2" alignItems="center">
                            <Text variant="body" size="large">
                                <b>Loading Service Entries...</b>
                            </Text>
                            <Text variant="body" subdued style={{ textAlign: 'center' }}>
                                Please wait a moment while we fetch your service entries.
                            </Text>
                        </Flex>
                        <Button appearance="secondary" size="large" onClick={handleGoBack}>
                            Back to previous page
                        </Button>
                    </Flex>
                </Content>
            );
        }

        if (fetchServiceEntriesRequestState === RequestState.Error) {
            return (
                <ErrorState
                    title="Unable to load Service Entries"
                    description="Something went wrong. Please try again."
                    onRetry={handleTryAgain}
                    onGoBack={handleGoBack}
                />
            );
        }

        if (
            fetchServiceEntriesRequestState === RequestState.Success &&
            serviceEntries.length === 0
        ) {
            return (
                <Page>
                    <Page.Header title="Service Entries" />
                    <Page.Content className="a2-bg-strong">
                        <Content justifyContent="center" alignItems="center">
                            <Flex direction="column" gap="5" alignItems="center">
                                <Flex direction="column" gap="2" alignItems="center">
                                    <Text variant="body" size="large">
                                        <b>No Service Entries</b>
                                    </Text>
                                    <Text variant="body" subdued style={{ textAlign: 'center' }}>
                                        You don't have any service entries yet. Create your first
                                        one to get started.
                                    </Text>
                                </Flex>
                                <Button
                                    appearance="primary"
                                    size="large"
                                    onClick={() => history.push('/service-entries/new')}
                                >
                                    Create Service Entry
                                </Button>
                            </Flex>
                        </Content>
                    </Page.Content>
                </Page>
            );
        }

        return (
            <Page>
                <Page.Header title="Service Entries" />
                <Page.Content className="a2-bg-strong">
                    <Fab horizontal="end" vertical="bottom" slot="fixed">
                        <FabButton onClick={() => history.push('/service-entries/new')}>
                            <FabIcon icon={ionIcons.add}></FabIcon>
                        </FabButton>
                    </Fab>
                    <Content>
                        <Text variant="eyebrow" className="a2-m-block-start-4 a2-m-block-end-2">
                            Service Entries
                        </Text>
                        <Flex direction="column" gap="2">
                            {serviceEntries.map(serviceEntry => (
                                <Card
                                    key={serviceEntry.id}
                                    padding="0"
                                    flexDirection="column"
                                    className="a2-p-inline-start-5"
                                >
                                    <Flex
                                        gap="4"
                                        className="a2-p-block-4 a2-p-inline-end-5"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        onClick={() =>
                                            history.push(`/service-entries/${serviceEntry.id}`)
                                        }
                                        aria-label="Preview service entry"
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <Flex direction="column" gap="1">
                                            <Text variant="body">
                                                {serviceEntry.firstServiceType}
                                            </Text>
                                            <Text variant="body" subdued size="small">
                                                {serviceEntry.vehicleName}
                                            </Text>
                                        </Flex>
                                        <Flex alignItems="center" justifyContent="end" gap="4">
                                            <Text
                                                variant="body"
                                                style={{
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {serviceEntry.serviceDate}
                                            </Text>
                                            <Icon size="small" svg={IconArrowForwardIos} />
                                        </Flex>
                                    </Flex>
                                    {serviceEntry.numberOfServiceRecords ? (
                                        <Chip
                                            label={serviceEntry.numberOfServiceRecords}
                                            className="a2-m-block-end-2"
                                        />
                                    ) : null}
                                </Card>
                            ))}
                        </Flex>
                    </Content>
                </Page.Content>
            </Page>
        );
    })
);
